import React, {
  useRef,
  useEffect,
  useMemo,
  Fragment,
  forwardRef,
  useState,
} from "react";
import { useServiceStore } from "store";
import { GRAINS, pixelPerInch } from "constants/appConstant";
import Wrapper from "./headwall-components/wrapper";
import { HorizontalRuler, HospitalBed } from "components";
import {
  alterImageUrl,
  getConsoleDropPoints,
} from "config/calculations/calculation";

const ConsolePanel = forwardRef((props, ref) => {
  const {
    values,
    ruler,
    handleDragStart,
    handleonDragOver,
    handleDrop,
    handleDragEnter,
    dragEntered,
    wallLength,
    service,
    clickArrowRight,
    clickArrowLeft,
    showBedGraphic,
  } = props;
  const { serv, view_only, setService } = useServiceStore();
  const heightRef = useRef(null);
  const wrapperRef = useRef(null);
  const [customEvent, setCustomEvent] = useState(null);
  const [railInd, setRailInd] = useState(0);
  const [railDropBoxes, setRailDropBoxes] = useState([]);

  const { headwalls, width, ceiling_height, model, sub_model } = values;

  useEffect(() => {
    const startingPoint = 58.0357142857; //3.25 inches margin from panel starting
    const consoleWidth = (width.ft * 12 + width.in) / pixelPerInch;
    const dropPoints = getConsoleDropPoints(consoleWidth, startingPoint);
    setRailDropBoxes(dropPoints);
  }, [width]);

  //adding some extra pixels in outer div to handle scroll issue while for left and right chase
  const widthCaluclation = useMemo(() => {
    return Math.round((width.ft * 12 + width.in) / pixelPerInch) + 100;
  }, [width]);

  //chase width calculation
  const railWidth = useMemo(() => {
    return Math.round((width.ft * 12 + width.in) / pixelPerInch);
  }, [width]);

  const serviceWidth = (ser) => {
    // return ser.category === "Medical Gas Outlet"
    //   ? ser.gang_width / pixelPerInch
    //   : ser.width / pixelPerInch;
    return ser.width / pixelPerInch;
  };

  const servicePosition = (ser) => {
    if (ser?.is_slide && ser.name === "Rail Mounted")
      return `translate(${ser.positions?.x}px, ${90}px)`;
    else return `translate(${ser.positions?.x}px, ${0}px)`;
  };

  const generateStyle = (ser) => {
    const styleObj = {
      cursor: "grab",
      background:
        ser === serv
          ? view_only
            ? "rgba(207, 35, 35, 0.2)"
            : "transparent"
          : "transparent",
    };
    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      /Apple Computer/.test(navigator.vendor);
    if (isSafari) {
      styleObj.left = ser.positions.x;
      styleObj.top = ser?.is_slide ? "80px" : "0px";
    } else styleObj.transform = servicePosition(ser);
    return styleObj;
  };

  const wallHeight = useMemo(() => {
    return Math.round(
      (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch
    );
  }, [ceiling_height]);

  useEffect(() => {
    //to adjust scale for axiom
    // Select all elements with the class 'scale'
    if (ruler) {
      const firstElements = document.querySelector(".first");
      const nextElements = document.querySelectorAll(".next");

      // Update styles for 'first' elements
      firstElements.style.maxWidth = "220.285714285px";

      nextElements.forEach((element) => {
        element.style.maxWidth = "214.285714285px";
      });
      // Cleanup function to revert styles when component unmounts
      return () => {
        firstElements.style.maxWidth = "";
        nextElements.forEach((element) => {
          element.style.maxWidth = "";
        });
      };
    }
  }, [ruler, width]);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowUp":
        break;
      case "ArrowDown":
        break;
      case "ArrowLeft":
        view_only && clickArrowLeft(customEvent, serv, railInd, 0, "infinity");
        break;
      case "ArrowRight":
        view_only && clickArrowRight(customEvent, serv, railInd, 0, "infinity");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    let isDragging = false;

    const handleClickOutside = (e) => {
      const clickedInsideFlexItemsEnd = e.target.closest(
        ".flex.items-end".split(" ").join(".")
      );
      if (
        !isDragging &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        clickedInsideFlexItemsEnd == null
      ) {
        setService("", false);
      }
    };

    const handleDragStart = () => (isDragging = true);
    const handleDragEnd = () => (isDragging = false);

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside); // fixed
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside); // match above
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, [serv, view_only]);

  const rulerWidth = useMemo(() => {
    const totalInches = width.in;

    // Convert total inches to feet and inches
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return { ft: feet, in: inches };
  }, [width]);

  const getOccupiedDropIndexes = (rail) => {
    const occupied = new Set();
    const boxWidth = 40.1785714286;
    const serviceWidth = service?.service?.width / pixelPerInch;
    if (serviceWidth > 50) {
      //if image size is greater than approx 3 inch
      occupied.add(0);
      occupied.add(railDropBoxes.length - 1);
    }

    railDropBoxes.forEach((point, dropIndex) => {
      const dropPoint = point - 20.0892857143;
      rail.services.forEach((ser) => {
        const serviceWidth = ser.width / pixelPerInch;
        const serviceLeft = ser.positions.x;
        const serviceRight = serviceLeft + serviceWidth;

        const boxLeft = dropPoint;
        const boxRight = dropPoint + boxWidth;

        // --- Rule 1: Normal overlap check (same as your some(...) code)
        const overlaps = !(serviceRight <= boxLeft || serviceLeft >= boxRight);
        if (overlaps) {
          occupied.add(dropIndex);
        }

        // --- Rule 2: Medical Gas outlet → block neighbors
        if (ser.category === "Medical Gas Outlet" && overlaps) {
          occupied.add(dropIndex - 1);
          occupied.add(dropIndex + 1);
        }
      });
    });

    return occupied;
  };

  return (
    <>
      <Wrapper
        ruler={ruler}
        width={widthCaluclation}
        wallHeight={wallHeight}
        wallLength={wallLength}
        railWidth={railWidth}
        ceiling_height={ceiling_height}
        model={model}
        ref={ref}
      >
        <div
          className={`h-full flex justify-center ${
            wallLength > railWidth + 20 ? "justify-center" : ""
          }`}
        >
          <div
            id="infinity-board"
            className="h-full flex relative justify-center"
          >
            <div
              className={`flex flex-col h-full relative justify-end `}
              ref={heightRef}
              style={{
                width: railWidth,
                height:
                  (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch,
              }}
            >
              <>
                {headwalls?.map((rail, railIndex) => {
                  const occupiedIndexes = getOccupiedDropIndexes(rail);
                  return (
                    <Fragment>
                      <div
                        key={railIndex}
                        className={`w-max ${
                          sub_model === "standard"
                            ? "console_headwall_standard"
                            : "console_outer"
                        } rail_center`}
                        onDrop={(e) => handleDrop(e, "rail", railIndex)}
                        onDragOver={(e) => handleonDragOver(e, "rail")}
                        onDragEnter={handleDragEnter}
                      >
                        <div
                          className={`${
                            sub_model === "standard" ? "" : "console_headwall"
                          }`}
                        >
                          <div
                            className={`relative h-[95.4196428571px] flex w-full`}
                            style={{
                              backgroundColor: rail?.color,
                              width: railWidth,
                              backgroundImage: `url(${
                                GRAINS[rail?.background]
                              })`,
                            }}
                            onDrop={(e) => handleDrop(e, "rail", railIndex)}
                            onDragOver={(e) => handleonDragOver(e, "rail")}
                          >
                            {/* Dropboxes */}
                            {railDropBoxes.map((point, dropIndex) => {
                              const dropPoint = point - 20.0892857143; // X position of drop point subtracting 20px to move it to left
                              // const occupied = isDropPointOccupied(
                              //   rail,
                              //   dropPoint
                              // );
                              const occupied = occupiedIndexes.has(dropIndex);
                              return (
                                <>
                                  {(dragEntered || serv) && (
                                    <div
                                      key={dropIndex}
                                      className={`absolute top-0 h-full transition-all duration-200 ${
                                        occupied ? "" : "border border-blue-500"
                                      }`}
                                      style={{
                                        left: dropPoint,
                                        width: "40.1785714286px",
                                      }}
                                      onDragEnter={(e) => e.stopPropagation()}
                                    />
                                  )}
                                </>
                              );
                            })}

                            {rail.services.map((ser, serviceIndex) => (
                              <Fragment>
                                <div
                                  key={serviceIndex}
                                  draggable={true}
                                  draggableid={ser._id}
                                  className={`service_main absolute py-1 flex justify-center h-full items-center ${
                                    dragEntered &&
                                    !service.service.is_accessory_rail &&
                                    !service.service.is_accessory
                                      ? "non_dropable "
                                      : ""
                                  }`}
                                  style={generateStyle(ser)}
                                  onClick={(e) => {
                                    // const rect = e.target.getBoundingClientRect();
                                    setCustomEvent(e);
                                    setService(ser, true);
                                    setRailInd(railIndex);
                                  }}
                                  onDragStart={(e) =>
                                    handleDragStart(
                                      ser,
                                      e,
                                      railIndex,
                                      serviceIndex
                                    )
                                  }
                                  onDragEnter={(e) => e.stopPropagation()}
                                >
                                  <div
                                    draggable={"false"}
                                    className="service_img pointer-events-none"
                                    style={{
                                      width: serviceWidth(ser),
                                      backgroundImage: `url(${alterImageUrl(
                                        ser.image_url
                                      )})`,
                                      backgroundSize: "contain",
                                      backgroundRepeat: "no-repeat",
                                      backgroundPosition: "center",
                                    }}
                                    onDragEnter={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </Fragment>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  );
                })}
                {ruler && (
                  <HorizontalRuler
                    class_name={"w-full bottom-[14px] z-50"}
                    width={rulerWidth}
                  />
                )}
              </>
            </div>
          </div>
        </div>
        {showBedGraphic && <HospitalBed />}
      </Wrapper>
    </>
  );
});

export default ConsolePanel;
